import React, { useEffect, useRef, useState } from 'react';

import { FormAlert, RadioGroup, Text } from '@ui-library';
import type { IRadioOption } from '@ui-library';

import type { CustomStackPicker, StackPickerGroup } from '../../services/projectStacks';
import {
  StackPicks,
  backendGroup,
  conflictMessage,
  databaseGroup,
  needsMessage,
  noDatabaseHint,
  noServerWarning,
  noneLabel,
  optionOf,
  optionState,
  replacedBy,
  replacedNote,
  serverOption,
  unavailableReason,
  unmetNeeds,
  withoutGroups,
} from '../../utils/stackPicker';
import { StackInfo } from './StackInfo';

/** A Full stack option picked while a Frontend or a Backend is picked: it applies
 *  only once the user switches to it. */
interface PendingPick {
  group: string;
  option: string;
}

interface CustomStackFieldsProps {
  /** The Custom option's picker, from GET /projects/stacks. */
  picker: CustomStackPicker;
  /** What is picked: None in every group the first time Custom is chosen. */
  picks: StackPicks;
  onChange: (picks: StackPicks) => void;
}

/**
 * The Custom option's groups in the Create Project drawer, right under its row
 * (multi-stack step 3.14g, layout A of the owner's pick, 2026-10-20; the rules of
 * step 3.13's Custom tech stack window, which it replaces): a box per group in
 * the picker's order, each starting with its None choice, in a grid that gives
 * two columns in the 720px drawer and one on a narrow screen, and an
 * architecture group inside its parent's box once that is picked and when it has
 * options. It shows each rule of the picker before the create API would refuse
 * a stack: a Full stack option replacing Frontend and Backend applies only once
 * the user switches, and while one is picked the Frontend and Backend options
 * are off, with a note under each group's label saying why and how to choose
 * one; an option whose needed group is empty notes it; a database without glue
 * for the picked server can't be picked; a database without a server is allowed
 * with a warning. It has no buttons of its own: Create Project applies what is
 * picked, and the drawer's footer names the stack.
 */
export const CustomStackFields: React.FC<CustomStackFieldsProps> = ({ picker, picks, onChange }) => {
  const [pending, setPending] = useState<PendingPick | null>(null);
  // Switch's button goes once it is pressed, so focus moves to the option it picked.
  const focusAfterSwitch = useRef<string | null>(null);
  useEffect(() => {
    if (!focusAfterSwitch.current) return;
    document.getElementById(focusAfterSwitch.current)?.focus();
    focusAfterSwitch.current = null;
  });

  const unmet = unmetNeeds(picker, picks);

  const radioName = (group: StackPickerGroup) => `custom-stack-${group.id}`;

  const choose = (group: StackPickerGroup, optionId: string) => {
    const option = optionOf(group, optionId);
    if (!option) {
      // None, the group's first option.
      setPending(null);
      onChange(withoutGroups(picker, picks, [group.id]));
      return;
    }
    const state = optionState(picker, picks, group, option);
    // A click on an option that is off does nothing, as its radio is disabled.
    if (state === 'off') return;
    setPending(state === 'held' ? { group: group.id, option: option.id } : null);
    if (state === 'applies') onChange({ ...picks, [group.id]: option.id });
  };

  const switchToPending = (group: StackPickerGroup, optionId: string) => {
    onChange({ ...withoutGroups(picker, picks, group.conflicts_with), [group.id]: optionId });
    setPending(null);
    focusAfterSwitch.current = `${radioName(group)}-${optionId}`;
  };

  // An architecture group shows under its parent, inside its box, once that is
  // picked, and only when it has options.
  const topGroups = picker.groups.filter((group) => !group.parent);
  const architecturesOf = (parent: StackPickerGroup) => picker.groups.filter(
    (group) => group.parent === parent.id && !!picks[parent.id] && group.options.length > 0,
  );
  const database = databaseGroup(picker);
  const backend = backendGroup(picker);
  const server = serverOption(picker, picks);

  // An option that is off is greyed and its radio disabled (optionState). One a
  // picked Full stack option keeps off has no reason of its own: the note under
  // its group's label says why.
  const radioOptions = (group: StackPickerGroup): IRadioOption[] => [
    {
      value: '',
      label: <Text variant="text-sm" weight="medium" color="secondary" as="span">{noneLabel(group)}</Text>,
    },
    ...group.options.map((option) => {
      const off = optionState(picker, picks, group, option) === 'off';
      const reason = unavailableReason(picker, picks, group, option);
      return {
        value: option.id,
        label: <Text variant="text-sm" weight="medium" color={off ? 'disabled' : 'secondary'} as="span">{option.label}</Text>,
        labelAddon: option.info ? <StackInfo label={option.label} lines={[option.info]} /> : undefined,
        disabled: off,
        description: reason || undefined,
      };
    }),
  ];

  const groupNotes = (group: StackPickerGroup) => {
    const notes: React.ReactNode[] = [];
    if (pending?.group === group.id) {
      const option = optionOf(group, pending.option);
      // Only while it is still held: a choice it would replace is still made.
      const held = !!option && optionState(picker, picks, group, option) === 'held';
      const message = option && held ? conflictMessage(picker, picks, group, option) : null;
      if (option && message) {
        notes.push(
          <FormAlert
            key="conflict"
            variant="warning"
            message={message}
            action={{ label: `Switch to ${option.label}`, onClick: () => switchToPending(group, option.id) }}
          />,
        );
      }
    }
    const needed = unmet.filter((need) => need.group.id === group.id);
    needed.forEach((need) => notes.push(
      <FormAlert key={`needs-${need.option.id}`} variant="warning" message={needsMessage(need)} />,
    ));
    if (group.id === database?.id) {
      if (picks[group.id] && !server && backend) {
        notes.push(<FormAlert key="no-server" variant="warning" message={noServerWarning(backend)} />);
      } else if (!picks[group.id] && needed.length === 0) {
        notes.push(<Text key="hint" variant="text-sm" color="tertiary">{noDatabaseHint(group)}</Text>);
      }
    }
    return notes;
  };

  const groupRadios = (group: StackPickerGroup) => {
    const fullstack = replacedBy(picker, picks, group);
    return (
      <RadioGroup
        name={radioName(group)}
        size="sm"
        label={<Text variant="text-sm" weight="semibold" color="primary" as="span">{group.label}</Text>}
        description={fullstack ? replacedNote(picker, group, fullstack) : undefined}
        value={picks[group.id] ?? ''}
        onChange={(optionId) => choose(group, optionId)}
        options={radioOptions(group)}
      />
    );
  };

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-bg-v2-lg">
      {topGroups.map((group) => (
        <div
          key={group.id}
          className="flex flex-col gap-bg-v2-lg min-w-0 p-bg-v2-xl border border-border-secondary rounded-bg-v2-xl"
        >
          {groupRadios(group)}
          {architecturesOf(group).map((architecture) => (
            <div key={architecture.id} className="flex flex-col gap-bg-v2-lg pl-bg-v2-3xl">
              {groupRadios(architecture)}
              {groupNotes(architecture)}
            </div>
          ))}
          {groupNotes(group)}
        </div>
      ))}
    </div>
  );
};
